import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, ArrowUp, Cookie, Check, Shield, BarChart3, Gift, Coffee } from 'lucide-react'
import { useSEO } from '@/hooks/useSEO'
import { usePrerenderReady } from '@/hooks/usePrerenderReady'
import { getConsent, revokeConsent } from '@/components/CookieBanner'
import { useLanguage } from '@/context/LanguageContext'
import Navbar from '@/components/landing/Navbar'

const content = {
  it: {
    seo: "Informativa sull'uso dei cookie sul sito Make me a Gift.",
    lastUpdated: '30 luglio 2026',
    back: 'Torna alla home',
    top: 'Torna su',
    preferences: 'Le tue preferenze',
    preferencesIntro: 'Gestisci quali cookie vuoi autorizzare. I cookie necessari sono sempre attivi.',
    necessary: 'Cookie necessari',
    alwaysActive: 'Sempre attivi',
    necessaryText: 'Autenticazione, sessione utente, sicurezza. Il sito non può funzionare senza.',
    analytics: 'Cookie analitici',
    active: 'Attivi',
    disabled: 'Disattivati',
    analyticsText: 'Statistiche aggregate di visita (pagine viste, tempo sul sito). Dati anonimi.',
    save: 'Salva preferenze',
    saved: 'Preferenze salvate',
    revoke: 'Revoca tutti i consensi',
    sections: [
      { title: '1. Introduzione', body: 'Questa informativa descrive i tipi di cookie utilizzati dal sito web Make me a Gift ("il Sito") e come l\'utente può controllarne l\'utilizzo. I cookie sono piccoli file di testo che vengono memorizzati sul dispositivo dell\'utente quando visita un sito web. Vengono ampiamente utilizzati per far funzionare i siti web in modo più efficiente e per fornire informazioni ai proprietari del sito.' },
      { title: '2. Tipi di cookie utilizzati', subsections: [
        { title: '2.1 Cookie necessari', body: 'Questi cookie sono essenziali per il corretto funzionamento del Sito. Permettono la navigazione tra le pagine, l\'accesso alle aree protette, la gestione della sessione utente e la memorizzazione delle preferenze. Senza questi cookie il Sito non potrebbe funzionare correttamente. Non richiedono consenso preventivo.', list: ['<b>Sessione:</b> memorizzano l\'identificativo di sessione per mantenere l\'utente collegato.', '<b>Autenticazione:</b> necessari per l\'area riservata (login, registrazione, dashboard).', '<b>Preferenze:</b> memorizzano le scelte dell\'utente (es. consenso ai cookie stessi).'] },
        { title: '2.2 Cookie analitici', body: 'Utilizziamo cookie analitici per raccogliere informazioni aggregate sull\'utilizzo del Sito, come le pagine più visitate, il tempo trascorso sul Sito e eventuali errori. Questi dati ci aiutano a migliorare l\'esperienza dell\'utente. Tali cookie vengono installati solo previo consenso esplicito dell\'utente.', list: ['<b>Statistiche di visita:</b> numero di visitatori, pagine viste, provenienza del traffico.', '<b>Prestazioni:</b> tempi di caricamento, errori di pagina.'] },
        { title: '2.3 Cookie di terze parti', body: 'Il Sito potrebbe utilizzare servizi di terze parti che impostano propri cookie. Tra questi:', list: ['<b>Google OAuth:</b> per l\'autenticazione tramite account Google.', '<b>Supabase:</b> per la gestione dell\'autenticazione e della sessione utente.'], after: 'Si raccomanda di consultare le rispettive informative privacy di questi servizi per maggiori dettagli.' },
        { title: '2.4 Cookie di profilazione', body: 'Il Sito non utilizza cookie di profilazione per finalità di marketing mirato. Nessun dato personale viene utilizzato per creare profili utente a fini pubblicitari.' },
      ]},
      { title: '3. Gestione del consenso', body: 'Al primo accesso al Sito, viene mostrato un banner informativo che richiede il consenso per l\'installazione di cookie non strettamente necessari. L\'utente può:', list: ['<b>Accettare tutti i cookie</b> cliccando su "Accetta tutti".', '<b>Rifiutare i cookie non essenziali</b> cliccando su "Rifiuta". Solo i cookie necessari verranno installati.', '<b>Personalizzare le preferenze</b> cliccando su "Personalizza preferenze" e selezionando singolarmente le categorie di cookie desiderate.', '<b>Modificare le preferenze in qualsiasi momento</b> utilizzando il pannello "Le tue preferenze" in cima a questa pagina.'], after: 'La scelta dell\'utente viene memorizzata per 6 mesi. Trascorso questo periodo, il banner verrà nuovamente visualizzato.' },
      { title: '4. Tabella riassuntiva dei cookie', table: { headers: ['Nome', 'Tipo', 'Durata', 'Scopo'], rows: [['sb-auth-token', 'Necessario', 'Sessione', 'Autenticazione Supabase'], ['cookie-consent-v2', 'Necessario', '6 mesi', "Memorizza le preferenze di consenso dell'utente"], ['Analytics', 'Analitico', '24 mesi', 'Statistiche aggregate di visita (solo previo consenso)']] } },
      { title: '5. Come disabilitare i cookie', body: 'L\'utente può gestire o disabilitare i cookie direttamente attraverso le impostazioni del proprio browser. Di seguito i link alle guide dei browser più comuni:', links: [{ label: 'Google Chrome', href: 'https://support.google.com/chrome/answer/95647' }, { label: 'Mozilla Firefox', href: 'https://support.mozilla.org/it/kb/Attivare%20e%20disattivare%20i%20cookie' }, { label: 'Safari', href: 'https://support.apple.com/it-it/guide/safari/sfri11471/mac' }, { label: 'Microsoft Edge', href: 'https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09' }], after: 'Si noti che la disabilitazione dei cookie necessari potrebbe compromettere il funzionamento del Sito e impedire l\'accesso ad alcune funzionalità.' },
      { title: "6. Trasferimento dati fuori dall'UE", body: 'Alcuni servizi di terze parti (es. Google) potrebbero trasferire dati al di fuori dello Spazio Economico Europeo (SEE). Tali trasferimenti avvengono sulla base di adeguate garanzie, come le clausole contrattuali tipo adottate dalla Commissione Europea, o in base a decisioni di adeguatezza.' },
      { title: "7. Diritti dell'utente", body: 'In conformità al Regolamento Generale sulla Protezione dei Dati (GDPR - UE 2016/679), l\'utente ha diritto di accedere ai propri dati, rettificarli, cancellarli, limitarne il trattamento e opporsi al loro utilizzo. Per esercitare questi diritti, è possibile scrivere a: aurarinoa@gmail.com' },
      { title: '8. Modifiche alla presente policy', body: 'La presente Cookie Policy può essere modificata in qualsiasi momento per riflettere cambiamenti nelle tecnologie utilizzate, nei requisiti legali o nelle pratiche del Sito. Le modifiche entrano in vigore dalla pubblicazione sul Sito.' },
      { title: '9. Contatti', body: 'Per qualsiasi domanda o richiesta relativa alla presente Cookie Policy, è possibile scrivere a: aurarinoa@gmail.com' },
    ],
  },
  en: {
    seo: 'Information about the use of cookies on the Make me a Gift website.',
    lastUpdated: 'July 30, 2026',
    back: 'Back to home',
    top: 'Back to top',
    preferences: 'Your preferences',
    preferencesIntro: 'Choose which cookies you authorize. Necessary cookies are always active.',
    necessary: 'Necessary cookies',
    alwaysActive: 'Always active',
    necessaryText: 'Authentication, user session and security. The site cannot operate without them.',
    analytics: 'Analytics cookies',
    active: 'Active',
    disabled: 'Disabled',
    analyticsText: 'Aggregate visit statistics (pages viewed and time on site). Anonymous data.',
    save: 'Save preferences',
    saved: 'Preferences saved',
    revoke: 'Revoke all consent',
    sections: [
      { title: '1. Introduction', body: 'This policy describes the types of cookies used by the Make me a Gift website ("the Site") and how the user can control their use. Cookies are small text files stored on the user\'s device when they visit a website. They are widely used to make websites work more efficiently and to provide information to site owners.' },
      { title: '2. Types of cookies used', subsections: [
        { title: '2.1 Necessary cookies', body: 'These cookies are essential for the proper functioning of the Site. They enable navigation between pages, access to protected areas, user session management and preference storage. Without these cookies the Site could not function properly. They do not require prior consent.', list: ['<b>Session:</b> store the session identifier to keep the user logged in.', '<b>Authentication:</b> required for the restricted area (login, registration, dashboard).', '<b>Preferences:</b> store user choices (e.g. cookie consent itself).'] },
        { title: '2.2 Analytics cookies', body: 'We use analytics cookies to collect aggregate information about the use of the Site, such as the most visited pages, time spent on the Site and any errors. This data helps us improve the user experience. These cookies are installed only with the user\'s explicit consent.', list: ['<b>Visit statistics:</b> number of visitors, pages viewed, traffic source.', '<b>Performance:</b> load times, page errors.'] },
        { title: '2.3 Third-party cookies', body: 'The Site may use third-party services that set their own cookies. These include:', list: ['<b>Google OAuth:</b> for authentication via Google account.', '<b>Supabase:</b> for managing authentication and user session.'], after: 'We recommend consulting the respective privacy policies of these services for more details.' },
        { title: '2.4 Profiling cookies', body: 'The Site does not use profiling cookies for targeted marketing purposes. No personal data is used to create user profiles for advertising.' },
      ]},
      { title: '3. Consent management', body: 'On first access to the Site, an informational banner is shown requesting consent for the installation of non-essential cookies. The user can:', list: ['<b>Accept all cookies</b> by clicking "Accept all".', '<b>Reject non-essential cookies</b> by clicking "Reject". Only necessary cookies will be installed.', '<b>Customize preferences</b> by clicking "Customize preferences" and selecting individual cookie categories.', '<b>Change preferences at any time</b> using the "Your preferences" panel at the top of this page.'], after: 'The user\'s choice is stored for 6 months. After this period, the banner will be shown again.' },
      { title: '4. Cookie summary table', table: { headers: ['Name', 'Type', 'Duration', 'Purpose'], rows: [['sb-auth-token', 'Necessary', 'Session', 'Supabase authentication'], ['cookie-consent-v2', 'Necessary', '6 months', "Stores the user's consent preferences"], ['Analytics', 'Analytics', '24 months', 'Aggregate visit statistics (only with consent)']] } },
      { title: '5. How to disable cookies', body: 'The user can manage or disable cookies directly through their browser settings. Below are links to the most common browser guides:', links: [{ label: 'Google Chrome', href: 'https://support.google.com/chrome/answer/95647' }, { label: 'Mozilla Firefox', href: 'https://support.mozilla.org/it/kb/Attivare%20e%20disattivare%20i%20cookie' }, { label: 'Safari', href: 'https://support.apple.com/it-it/guide/safari/sfri11471/mac' }, { label: 'Microsoft Edge', href: 'https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09' }], after: 'Note that disabling necessary cookies may compromise the functioning of the Site and prevent access to some features.' },
      { title: '6. Data transfers outside the EEA', body: 'Some third-party services (e.g. Google) may transfer data outside the European Economic Area (EEA). Such transfers take place on the basis of adequate safeguards, such as the Standard Contractual Clauses adopted by the European Commission, or based on adequacy decisions.' },
      { title: '7. User rights', body: 'In accordance with the General Data Protection Regulation (GDPR - EU 2016/679), the user has the right to access their data, rectify it, erase it, restrict its processing and object to its use. To exercise these rights, you can write to: aurarinoa@gmail.com' },
      { title: '8. Changes to this policy', body: 'This Cookie Policy may be modified at any time to reflect changes in the technologies used, legal requirements, or Site practices. Changes take effect upon publication on the Site.' },
      { title: '9. Contact', body: 'For any question or request regarding this Cookie Policy, you can write to: aurarinoa@gmail.com' },
    ],
  },
}

export default function CookiePolicyPage() {
  const { language, t } = useLanguage()
  const c = content[language]
  useSEO({ title: c.seo, description: c.seo })
  usePrerenderReady()

  const [analytics, setAnalytics] = useState(false)
  const [consentExists, setConsentExists] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    const stored = getConsent()
    if (stored) {
      setAnalytics(stored.analytics)
      setConsentExists(true)
    }
  }, [])

  const handleSave = () => {
    localStorage.setItem(
      'cookie-consent-v2',
      JSON.stringify({ necessary: true, analytics, expires: Date.now() + 180 * 24 * 60 * 60 * 1000 })
    )
    window.dispatchEvent(new Event('cookie-consent-changed'))
    setConsentExists(true)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  const handleRevoke = () => { revokeConsent() }

  return (
    <div className="min-h-screen bg-[#F5F7F9] flex flex-col">
      <Navbar />
      <div className="flex-1 flex items-center justify-center pt-20 sm:pt-24">
      <div className="max-w-3xl w-full mx-auto px-4 sm:px-6 py-8 sm:py-12 text-left">
        <div className="flex items-center gap-3 mb-6">
          <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-neutral-900 text-white">
            <Cookie className="w-5 h-5" />
          </span>
          <h1 className="text-3xl font-extrabold text-neutral-900">Cookie Policy</h1>
        </div>

        <p className="text-sm text-neutral-500 mb-10">{language === 'it' ? 'Ultimo aggiornamento' : 'Last updated'}: {c.lastUpdated}</p>

        {/* Preferences Panel */}
        <div className="rounded-2xl bg-white border border-neutral-200 shadow-sm p-5 sm:p-6 mb-10">
          <h2 className="text-lg font-bold text-neutral-900 mb-1">{c.preferences}</h2>
          <p className="text-sm text-neutral-500 mb-5">{c.preferencesIntro}</p>

          <div className="space-y-3">
            {/* Necessary */}
            <div className="flex items-start gap-3 rounded-xl bg-neutral-50 p-4">
              <span className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg bg-neutral-900 text-white">
                <Shield className="w-4 h-4" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-neutral-900">{c.necessary}</p>
                  <span className="text-[10px] font-medium text-neutral-400 bg-neutral-200 rounded-full px-2 py-0.5">{c.alwaysActive}</span>
                </div>
                <p className="text-xs text-neutral-500 leading-relaxed mt-1">{c.necessaryText}</p>
              </div>
              <div className="flex-shrink-0 relative inline-flex h-5 w-9 items-center rounded-full bg-neutral-900 opacity-60 cursor-not-allowed">
                <span className="inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow translate-x-5" />
              </div>
            </div>

            {/* Analytics */}
            <div className="flex items-start gap-3 rounded-xl bg-neutral-50 p-4">
              <span className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg bg-indigo-100 text-indigo-600">
                <BarChart3 className="w-4 h-4" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-neutral-900">{c.analytics}</p>
                  <span className={`text-[10px] font-medium rounded-full px-2 py-0.5 ${analytics ? 'text-green-700 bg-green-100' : 'text-neutral-400 bg-neutral-200'}`}>
                    {analytics ? c.active : c.disabled}
                  </span>
                </div>
                <p className="text-xs text-neutral-500 leading-relaxed mt-1">{c.analyticsText}</p>
              </div>
              <button
                type="button"
                onClick={() => setAnalytics((v) => !v)}
                className={`flex-shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${analytics ? 'bg-neutral-900' : 'bg-neutral-300'}`}
                aria-label="Toggle analytics cookies"
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${analytics ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 mt-5">
            <button
              type="button"
              onClick={handleSave}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-neutral-800"
            >
              {saved ? (<><Check className="w-4 h-4" />{c.saved}</>) : c.save}
            </button>
            {consentExists && (
              <button
                type="button"
                onClick={handleRevoke}
                className="flex-1 inline-flex items-center justify-center rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-medium text-neutral-700 transition hover:bg-neutral-50"
              >
                {c.revoke}
              </button>
            )}
          </div>
        </div>

        {/* Full Policy Text */}
        <div className="prose prose-neutral max-w-none space-y-8">
          {c.sections.map((s, i) => (
            <section key={i}>
              <h2 className="text-xl font-bold text-neutral-900 mb-3">{s.title}</h2>
              {s.body && <p className="text-neutral-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: s.body }} />}
              {s.subsections?.map((sub, j) => (
                <div key={j}>
                  <h3 className="text-lg font-semibold text-neutral-800 mt-5 mb-2">{sub.title}</h3>
                  <p className="text-neutral-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: sub.body }} />
                  {sub.list && (
                    <ul className="list-disc list-inside text-neutral-700 mt-2 space-y-1">
                      {sub.list.map((item, k) => <li key={k} dangerouslySetInnerHTML={{ __html: item }} />)}
                    </ul>
                  )}
                  {sub.after && <p className="text-neutral-700 leading-relaxed mt-2" dangerouslySetInnerHTML={{ __html: sub.after }} />}
                </div>
              ))}
              {s.list && (
                <ul className="list-disc list-inside text-neutral-700 mt-2 space-y-1">
                  {s.list.map((item, j) => <li key={j} dangerouslySetInnerHTML={{ __html: item }} />)}
                </ul>
              )}
              {s.after && <p className="text-neutral-700 leading-relaxed mt-2" dangerouslySetInnerHTML={{ __html: s.after }} />}
              {s.table && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-neutral-200 rounded-lg">
                    <thead className="bg-neutral-100">
                      <tr>
                        {s.table.headers.map((h, j) => <th key={j} className="text-left px-4 py-3 font-semibold text-neutral-800 border-b border-neutral-200">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {s.table.rows.map((row, j) => (
                        <tr key={j} className={j < s.table!.rows.length - 1 ? 'border-b border-neutral-100' : ''}>
                          {row.map((cell, k) => <td key={k} className="px-4 py-3 text-neutral-700">{cell}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {s.links && (
                <>
                  <ul className="list-disc list-inside text-neutral-700 mt-2 space-y-1">
                    {s.links.map((ln, j) => <li key={j}><a href={ln.href} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">{ln.label}</a></li>)}
                  </ul>
                </>
              )}
            </section>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-neutral-200 flex flex-row justify-center gap-3">
          <Link to="/" className="inline-flex items-center gap-2 rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-neutral-800">
            <ArrowLeft className="h-4 w-4" />
            {c.back}
          </Link>
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="inline-flex items-center gap-2 rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-medium text-neutral-700 transition hover:bg-neutral-50">
            <ArrowUp className="h-4 w-4" />
            {c.top}
          </button>
        </div>
      </div>
      </div>

      <footer className="bg-neutral-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 items-start sm:items-start text-center sm:text-left">
            <div className="flex flex-col gap-3 items-center sm:items-start">
              <Link to="/" className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-lg bg-white text-neutral-900 flex items-center justify-center">
                  <Gift className="w-4 h-4" />
                </span>
                <span className="text-lg font-semibold font-logo">Make me a Gift</span>
              </Link>
              <p className="text-sm text-neutral-400 max-w-xs leading-relaxed">{t('footer.description')}</p>
              <div className="flex items-center gap-4 text-sm pt-1">
                <Link to="/cookie-policy" className="text-neutral-300 hover:text-white transition-colors">Cookie</Link>
                <span className="text-neutral-600">·</span>
                <Link to="/privacy" className="text-neutral-300 hover:text-white transition-colors">Privacy</Link>
                <span className="text-neutral-600">·</span>
                <Link to="/terms" className="text-neutral-300 hover:text-white transition-colors">{t('footer.terms')}</Link>
                <span className="text-neutral-600">·</span>
                <Link to="/about" className="text-neutral-300 hover:text-white transition-colors">About</Link>
              </div>
            </div>

            <div className="flex flex-col gap-3 items-center sm:items-start">
              <p className="text-sm font-semibold text-white">{t('footer.support.heading')}</p>
              <p className="text-sm text-neutral-400 leading-relaxed max-w-xs">{t('footer.support.text')}</p>
              <a href="https://ko-fi.com/aurarinoa" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#FF5E5B] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#e54e4b] w-fit">
                <Coffee className="h-4 w-4" />
                {t('footer.support.button')}
              </a>
            </div>

            <div className="flex flex-col gap-3 items-center sm:items-start lg:items-end">
              <div className="flex items-center">
                <p className="text-sm font-semibold text-white mr-2">{t('footer.madeWith')}</p><a href="https://coderblock.ai/" target="_blank" rel="noreferrer" className="transition-opacity hover:opacity-100 inline-flex items-center"><img src="/images/coderblock.svg" alt="Coderblock" className="h-5 w-auto brightness-0 invert opacity-70" /></a>
              </div>
              <p className="text-sm text-neutral-400 leading-relaxed max-w-xs lg:text-right">
                {t('footer.idea')} <strong>Aura Nuccio</strong>
              </p>
              <div className="flex items-center gap-4 text-sm pt-1">
                <Link to="/login" className="text-neutral-300 hover:text-white transition-colors">{t('footer.login')}</Link>
                <span className="text-neutral-600">·</span>
                <Link to="/register" className="text-neutral-300 hover:text-white transition-colors">{t('footer.register')}</Link>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-2 text-center sm:text-left">
            <p className="text-xs text-neutral-500">© {new Date().getFullYear()} Make me a Gift. {t('footer.rights')}</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
